import { useEffect, useRef, useState } from "react";

import { TABLE_OF_CONTENTS } from "../constants/overviewConstants";

type NavItem = {
  id: string;
  label: string;
};

const createUrlSlug = (text: string): string =>
  text
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const isSectionInView = (elementId: string): boolean => {
  const element = document.getElementById(elementId);

  if (!element) return false;

  const { top, bottom } = element.getBoundingClientRect();

  return (
    top <= TABLE_OF_CONTENTS.SCROLL_THRESHOLD &&
    bottom >= TABLE_OF_CONTENTS.VIEWPORT_THRESHOLD
  );
};

export const getDocumentTop = (element: HTMLElement): number => {
  let top = 0;
  let node: Element | null = element;

  while (node instanceof HTMLElement) {
    top += node.offsetTop;
    node = node.offsetParent;
  }

  return top;
};

const scrollToSectionTop = (element: HTMLElement): void => {
  window.scrollTo({
    top: getDocumentTop(element) - TABLE_OF_CONTENTS.STICKY_TOP_OFFSET,
    behavior: "smooth",
  });
};

const scrollToElement = (elementId: string, label: string): void => {
  const element = document.getElementById(elementId);

  if (!element) return;

  const urlSlug = createUrlSlug(label);
  window.history.pushState(null, "", `#${urlSlug}`);
  scrollToSectionTop(element);
};

type UseTableOfContentsOptions = {
  navItems: NavItem[];
  isLoading?: boolean;
};

export const useTableOfContents = ({
  navItems,
  isLoading,
}: UseTableOfContentsOptions) => {
  const [activeSection, setActiveSection] = useState<string>("");
  const hasScrolledToHash = useRef(false);
  const suppressScrollSpyUntil = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      if (Date.now() < suppressScrollSpyUntil.current) return;

      for (const item of navItems) {
        if (isSectionInView(item.id)) {
          setActiveSection(item.id);

          return;
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    const timeoutId = setTimeout(() => {
      handleScroll();
    }, TABLE_OF_CONTENTS.INITIAL_SCROLL_DELAY);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [navItems]);

  useEffect(() => {
    if (isLoading || navItems.length === 0 || hasScrolledToHash.current) return;

    const hash = window.location.hash.slice(1);

    if (!hash) {
      hasScrolledToHash.current = true;

      return;
    }

    const target = navItems.find((item) => createUrlSlug(item.label) === hash);
    const element = target ? document.getElementById(target.id) : null;

    if (target && element) {
      requestAnimationFrame(() => {
        scrollToSectionTop(element);
        setActiveSection(target.id);
        hasScrolledToHash.current = true;
      });
    }
  }, [navItems, isLoading]);

  const scrollToSection = (sectionId: string, sectionLabel: string) => {
    scrollToElement(sectionId, sectionLabel);
    setActiveSection(sectionId);
    suppressScrollSpyUntil.current =
      Date.now() + TABLE_OF_CONTENTS.SCROLL_SPY_SUPPRESSION_MS;
  };

  return {
    activeSection: activeSection || navItems[0]?.id,
    scrollToSection,
  };
};

export const useSticky = () => {
  const [isSticky, setIsSticky] = useState(false);
  const containerRef = useRef<HTMLElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current && placeholderRef.current) {
        const containerRect = placeholderRef.current.getBoundingClientRect();
        const shouldStick =
          containerRect.top <= TABLE_OF_CONTENTS.STICKY_TOP_OFFSET;
        setIsSticky(shouldStick);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return {
    isSticky,
    containerRef,
    placeholderRef,
  };
};
